import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ChannelItem, ChannelItemType } from 'types/channel';
import {
  getChannelItems,
  reorderItems,
  removeChannelItem,
  GetChannelItemsResponse,
} from '../api/marketplaceApi';

export interface UseChannelContentManageResult {
  items: ChannelItem[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  reorder: (itemIds: string[]) => Promise<boolean>;
  isReordering: boolean;
  remove: (itemId: string) => Promise<boolean>;
  isRemoving: boolean;
}

export function useChannelContentManage(
  channelId: string | null,
  options?: { itemType?: ChannelItemType }
): UseChannelContentManageResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [items, setItems] = useState<ChannelItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isReordering, setIsReordering] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !channelId) {
      setItems([]);
      setTotal(0);
      return;
    }

    let cancelled = false;

    const fetchItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: GetChannelItemsResponse = await getChannelItems(
          apiClients.schemaClient,
          channelId,
          { itemType: options?.itemType }
        );

        if (!cancelled) {
          setItems(response.data);
          setTotal(response.total);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load items';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchItems();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, channelId, options?.itemType, fetchTrigger]);

  const reorder = useCallback(
    async (itemIds: string[]): Promise<boolean> => {
      if (!apiClients || !organizationId || !channelId) return false;

      setIsReordering(true);
      setError(null);

      const previous = [...items];
      const reordered = itemIds
        .map((id) => items.find((item) => item.id === id))
        .filter((item): item is ChannelItem => item !== undefined);
      setItems(reordered);

      try {
        await reorderItems(
          apiClients.schemaClient,
          channelId,
          itemIds
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reorder items';
        setError(message);
        setItems(previous);
        return false;
      } finally {
        setIsReordering(false);
      }
    },
    [apiClients, organizationId, channelId, items]
  );

  const remove = useCallback(
    async (itemId: string): Promise<boolean> => {
      if (!apiClients || !organizationId || !channelId) return false;

      setIsRemoving(true);
      setError(null);

      const previous = [...items];
      setItems(items.filter((item) => item.id !== itemId));
      setTotal((prev) => Math.max(0, prev - 1));

      try {
        await removeChannelItem(
          apiClients.schemaClient,
          channelId,
          itemId
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to remove item';
        setError(message);
        setItems(previous);
        setTotal(previous.length);
        return false;
      } finally {
        setIsRemoving(false);
      }
    },
    [apiClients, organizationId, channelId, items]
  );

  return {
    items,
    total,
    loading,
    error,
    refetch,
    reorder,
    isReordering,
    remove,
    isRemoving,
  };
}
